import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import AdminDashboard from './pages/AdminDashboard';
import GuardDashboard from './pages/GuardDashboard';
import NetworkView from './pages/NetworkView';
import OpsecPage from './pages/opsec';
import ClientDashboard from './pages/ClientDashboard';
import LoginPage from './pages/LoginPage';
import SettingsPage from './pages/Settings';
import CivilRespond from './pages/civilRespond';
import CivilRespondCheck from './pages/civilRespondCheck';
import CivilRespondActions from './pages/civilRespondActions';
import MissingPersonsAdmin from './pages/missingPersonsAdmin';
import MostWantedAdmin from './pages/mostWantedAdmin';
import StolenVehiclesAdmin from './pages/stolenVehiclesAdmin';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import PushHandler from './components/PushHandler';
import UpdatePrompt from './components/UpdatePrompt';
import OfflineIndicator from './components/OfflineIndicator';

// =====================================================================
// iSentiAlert (D15) — Toast listener for the 'isentialert-linked' event.
//
// AuthContext dispatches this event after a successful pair-claim
// (the PWA's piggyback claim against a pending iSentiAlert device on
// this user's account). When fired, we show a 3-second toast at the
// top of the screen confirming the link.
//
// The toast is intentionally simple — a single global event, a single
// state hook, a single render. No portal, no context, no queueing. The
// event is rare (only fires on successful pairing) and the toast is
// short-lived (3s) so overlap is not a real concern in v1.
//
// If the same event fires twice within 3s, the toast is replaced (not
// stacked). This avoids visual clutter if the user logs in/out quickly.
// =====================================================================
function IsentialertToastListener() {
  const { t } = useTranslation();
  const [toast, setToast] = useState<{ text: string; nonce: number } | null>(null);

  useEffect(() => {
    const handler = (e: Event) => {
      // CustomEvent detail is unknown here, but the text comes from
      // the i18n key. We just need to know "an event happened".
      const text = (e as CustomEvent).detail?.text
        || t('alertApp.linked')
        || 'iSentiAlert linked on this device';
      // Bump the nonce so even if the timer hasn't fired yet, the
      // existing toast is replaced and its timer restarted.
      setToast({ text, nonce: Date.now() });
    };
    window.addEventListener('isentialert-linked', handler as EventListener);
    return () => window.removeEventListener('isentialert-linked', handler as EventListener);
  }, [t]);

  useEffect(() => {
    if (!toast) return;
    const id = setTimeout(() => {
      setToast((current) => (current && current.nonce === toast.nonce ? null : current));
    }, 3000);
    return () => clearTimeout(id);
  }, [toast]);

  if (!toast) return null;

  return (
    <div
      data-testid="isentialert-linked-toast"
      className="fixed top-20 left-1/2 -translate-x-1/2 z-[100] px-5 py-3 rounded-lg shadow-2xl text-base font-medium max-w-[90vw] flex items-center gap-2 bg-green-600 text-white border-2 border-green-400"
      role="status"
      aria-live="polite"
    >
      <span aria-hidden="true">✓</span>
      <span>{toast.text}</span>
    </div>
  );
}

function OrientationLock() {
  const [isLandscape, setIsLandscape] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Detect mobile device via user agent
    const ua = navigator.userAgent;
    const mobile = /Mobi|Android|iPhone|iPad|iPod|webOS/i.test(ua);
    setIsMobile(mobile);

    if (!mobile) return; // no need to listen if not mobile

    const checkOrientation = () => {
      setIsLandscape(window.innerWidth > window.innerHeight);
    };
    checkOrientation();
    window.addEventListener('resize', checkOrientation);
    return () => window.removeEventListener('resize', checkOrientation);
  }, []);

  if (!isMobile || !isLandscape) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-background flex items-center justify-center">
      <div className="text-center p-6">
        <h1 className="text-2xl font-bold mb-2">Please rotate your device</h1>
        <p className="text-muted-foreground">This app works best in portrait mode.</p>
      </div>
    </div>
  );
}

// v0.4.9.2.4c5: 'tadmin' added — TAdmin (Technical Admin) is an
// iSentinel360 employee role, parallel to sadmin. Without it here,
// every `allowedRoles` comparison silently excludes TAdmin.
type Role = 'admin' | 'guard' | 'cadmin' | 'superadmin' | 'sadmin' | 'tadmin' | 'agent' | 'agentAdmin';

function PrivateRoute({ children, allowedRoles }: { children: React.ReactNode; allowedRoles?: ReadonlyArray<Role> }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center h-screen">Loading...</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role as Role)) return <Navigate to="/" replace />;
  return children;
}

// Smart post-login redirect: civil respondents go to their entry page,
// commercial users go to /guard.
//
// v0.4.9.2.3c11: route by ROLE, not user_type.
//
// The previous implementation routed by `user.user_type === 'civil_respondent'`.
// For PASSWORD login, the server's /api/auth/login response includes
// `user_type`, so this check works. For PASSKEY login, the server's
// /api/auth/passkey/login/verify response does NOT include `user_type`,
// so the check fails and falls through to `Navigate to="/guard"`.
//
// /guard's PrivateRoute then rejects the user (role='agent' is not
// in allowedRoles), redirecting them back to '/'. The cycle repeats:
// /guard → / → /guard → / → ... and the user lands on '/' which
// renders PostLoginRedirect again — infinite flicker between '/'
// and '/guard'. This is the cause of the white screen on passkey
// login (mobile AND desktop).
//
// Fix: route by ROLE which is ALWAYS present in the user object
// (it's required for the JWT and authorization).
function PostLoginRedirect() {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  const role = user.role;
  // Civil respondents (agents) → /civil-respondent
  if (role === 'agent' || role === 'agentAdmin') {
    return <Navigate to="/civil-respondent" replace />;
  }
  // Everyone else (admin, cadmin, guard, superadmin, sadmin) → /guard
  return <Navigate to="/guard" replace />;
}

function App() {
  return (
    <BrowserRouter>
      <OrientationLock />
      <PushHandler />
      <UpdatePrompt />
      <OfflineIndicator />
      <IsentialertToastListener />
      <Routes>
        <Route path="/login" element={<LoginPage />} />

        {/* Post-login redirect: sends civil_respondent users to their entry,
            everyone else to /guard. */}
        <Route
          path="/"
          element={
            <PrivateRoute>
              <PostLoginRedirect />
            </PrivateRoute>
          }
        />

        <Route
          path="/admin"
          element={
            <PrivateRoute allowedRoles={['admin', 'cadmin', 'superadmin', 'sadmin']}>
              <AdminDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/guard"
          element={
            // v0.4.9.2.4c5: TAdmin added — they verify an install by
            // watching alerts fire in the GuardDashboard.
            <PrivateRoute allowedRoles={['admin', 'cadmin', 'guard', 'superadmin', 'sadmin', 'tadmin']}>
              <GuardDashboard />
            </PrivateRoute>
          }
        />
        <Route
          path="/client"
          element={
            <PrivateRoute allowedRoles={['cadmin', 'superadmin', 'sadmin']}>
              <ClientDashboard />
            </PrivateRoute>
          }
        />
        {/* v0.4.9.2.4c5: TAdmin added — provisioning Edge Servers and
            installing Cameras is their job. NetworkView carries its own
            employee-only guard, and its store Manage modal is split so a
            TAdmin only ever sees the Provisioning tab. */}
        <Route
          path="/network"
          element={
            <PrivateRoute allowedRoles={['superadmin', 'sadmin', 'tadmin']}>
              <NetworkView />
            </PrivateRoute>
          }
        />

        <Route
          path="/opsec"
          element={
            <PrivateRoute allowedRoles={['superadmin', 'sadmin']}>
              <OpsecPage />
            </PrivateRoute>
          }
        />

        {/* Civil respondent — user-facing pages (entry + check flow + history). */}
        <Route
          path="/civil-respondent"
          element={
            <PrivateRoute allowedRoles={['agent', 'agentAdmin', 'superadmin', 'sadmin']}>
              <CivilRespond />
            </PrivateRoute>
          }
        />
        <Route
          path="/civil-respondent/check/:targetType"
          element={
            <PrivateRoute allowedRoles={['agent', 'agentAdmin', 'superadmin', 'sadmin']}>
              <CivilRespondCheck />
            </PrivateRoute>
          }
        />
        <Route
          path="/civil-respondent/actions"
          element={
            <PrivateRoute allowedRoles={['agent', 'agentAdmin', 'superadmin', 'sadmin']}>
              <CivilRespondActions />
            </PrivateRoute>
          }
        />

        {/* Civil respondent — admin pages for managing the 3 databases.
            Top-level routes for clarity. agentAdmin (or superadmin/sadmin
            for testing) can add/remove entries. */}
        <Route
          path="/missing-persons"
          element={
            <PrivateRoute allowedRoles={['agentAdmin', 'superadmin', 'sadmin']}>
              <MissingPersonsAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/most-wanted"
          element={
            <PrivateRoute allowedRoles={['agentAdmin', 'superadmin', 'sadmin']}>
              <MostWantedAdmin />
            </PrivateRoute>
          }
        />
        <Route
          path="/stolen-vehicles"
          element={
            <PrivateRoute allowedRoles={['agentAdmin', 'superadmin', 'sadmin']}>
              <StolenVehiclesAdmin />
            </PrivateRoute>
          }
        />

        {/* Settings — every authenticated user can access their own. */}
        <Route
          path="/settings"
          element={
            <PrivateRoute>
              <SettingsPage />
            </PrivateRoute>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
